import { Card, Table, Tag, Typography } from "antd";
import Header from "../_shared/components/Header";

const { Text, Paragraph } = Typography;

const endpoints = [
  { method: "GET", path: "/api/scheduling/tasks/", desc: "List all tasks" },
  { method: "POST", path: "/api/scheduling/tasks/", desc: "Create a new task" },
  { method: "GET", path: "/api/scheduling/tasks/{id}/", desc: "Get task detail" },
  { method: "PATCH", path: "/api/scheduling/tasks/{id}/", desc: "Update a task" },
  { method: "DELETE", path: "/api/scheduling/tasks/{id}/", desc: "Delete a task" },
  { method: "GET", path: "/api/scheduling/staff-members/", desc: "List all staff members" },
  { method: "POST", path: "/api/scheduling/staff-members/", desc: "Create a staff member" },
  { method: "GET", path: "/api/scheduling/staff-members/{id}/", desc: "Get staff member detail with schedule" },
  { method: "GET", path: "/api/scheduling/machines/", desc: "List all machines" },
  { method: "POST", path: "/api/scheduling/machines/", desc: "Create a machine" },
  { method: "GET", path: "/api/scheduling/jobs/", desc: "List all jobs" },
  { method: "POST", path: "/api/scheduling/jobs/", desc: "Create a new job (from external system)" },
  { method: "GET", path: "/api/scheduling/jobs/{id}/", desc: "Get job detail with tasks and exceptions" },
  { method: "PATCH", path: "/api/scheduling/jobs/{id}/", desc: "Update a job" },
  { method: "DELETE", path: "/api/scheduling/jobs/{id}/", desc: "Delete a job" },
  { method: "GET", path: "/api/scheduling/job-tasks/", desc: "List job tasks (filterable by job, task, status)" },
  { method: "PATCH", path: "/api/scheduling/job-tasks/{id}/", desc: "Update job task (status, percent_complete)" },
  { method: "GET", path: "/api/scheduling/job-exceptions/", desc: "List scheduling exceptions" },
  { method: "GET", path: "/api/scheduling/queues/", desc: "List all task queues with counts" },
  { method: "GET", path: "/api/scheduling/queues/{task_pk}/", desc: "Get queue items for a specific task" },
  { method: "GET", path: "/api/scheduling/capacity/", desc: "Get capacity data (params: view, pass, date)" },
  { method: "POST", path: "/api/scheduling/run/", desc: "Trigger a full scheduling run" },
  { method: "GET", path: "/api/scheduling/dashboard/", desc: "Get dashboard summary data" },
  { method: "GET", path: "/api/scheduling/holidays/", desc: "List holidays" },
  { method: "POST", path: "/api/scheduling/holidays/", desc: "Create a holiday" },
  { method: "GET", path: "/api/scheduling/business-hours/", desc: "List business hours" },
  { method: "PATCH", path: "/api/scheduling/business-hours/{id}/", desc: "Update business hours" },
  { method: "GET", path: "/api/scheduling/audit-log/", desc: "List audit log entries" },
  { method: "GET", path: "/api/scheduling/job-variable-definitions/", desc: "List job variable definitions" },
  { method: "POST", path: "/api/auth/login/", desc: "Obtain JWT token (email + password)" },
  { method: "POST", path: "/api/auth/refresh/", desc: "Refresh JWT token" },
];

const METHOD_COLORS: Record<string, string> = {
  GET: "blue", POST: "green", PATCH: "orange", PUT: "gold", DELETE: "red",
};

export default function ApiDocs() {
  const columns = [
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      width: 80,
      render: (v: string) => <Tag color={METHOD_COLORS[v]}>{v}</Tag>,
    },
    { title: "Endpoint", dataIndex: "path", key: "path", render: (v: string) => <Text code>{v}</Text> },
    { title: "Description", dataIndex: "desc", key: "desc" },
  ];

  return (
    <div className="w-full p-4">
      <Header title="API Documentation" />

      <Card className="mb-4">
        <Paragraph>
          All API endpoints require JWT authentication. Include the token in the <Text code>Authorization</Text> header:
        </Paragraph>
        <Paragraph>
          <Text code>Authorization: Bearer {'<token>'}</Text>
        </Paragraph>
        <Paragraph>
          To obtain a token, POST to <Text code>/api/auth/login/</Text> with <Text code>{"{ email, password }"}</Text>.
        </Paragraph>
        <Paragraph>
          <strong>Creating Jobs from External Systems:</strong> POST to <Text code>/api/scheduling/jobs/</Text> with job data
          including <Text code>required_task_ids</Text> (array of task PKs). The system auto-creates job tasks, calculates
          adjusted completion date, and assigns job mix.
        </Paragraph>
        <Paragraph>
          <strong>Updating Task Progress:</strong> PATCH to <Text code>/api/scheduling/job-tasks/{'{id}/'}</Text> with
          <Text code>{"{ status, percent_complete }"}</Text> to report task progress from the ERP system.
        </Paragraph>
      </Card>

      <Table dataSource={endpoints} columns={columns} rowKey="path" size="small" pagination={false} />
    </div>
  );
}
